import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getFinanceSummary } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, SectionHeader } from '@/components/banking';
import { CardSkeleton, EmptyState, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { formatPercentageChange } from '@/services/formatters';

/**
 * Mobile reports and finance summaries.
 *
 * Compact visualisation only: a horizontal bar list, which stays readable at
 * 320px and at 200% text where a chart would not. Anything needing a wide
 * table or custom columns hands off to SME Web with its reason stated.
 */

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;

export function ReportsPage() {
  const [params] = useSearchParams();
  const resolved = resolveContext();
  const [days, setDays] = useState<number>(30);

  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const summary = useAsync(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (await getFinanceSummary(companyId, userId, days)).data;
  }, [companyId, userId, days]);

  if (!resolved) return null;

  const canExport = resolveCapability(resolved.entitlements, CAPABILITIES.reportsExport).allowed;
  const view = params.get('view');

  const s = summary.data;
  const maxCategory = Math.max(1, ...(s?.byCategory ?? []).map((c) => c.amountMinor));

  return (
    <>
      <MobileHeader
        title={view === 'treasury' ? 'Treasury' : view === 'invoices' ? 'Invoices' : 'Reports'}
        backTo={view ? '/mobile/home' : '/mobile/more'}
        backLabel={view ? 'Home' : 'More'}
      />

      <div className="tm-stack">
        <div className="tm-chips" role="group" aria-label="Reporting period">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`tm-chip${days === r.days ? ' tm-chip--on' : ''}`}
              aria-pressed={days === r.days}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>

        {summary.status === 'loading' ? (
          <LoadingRegion label="Loading your summary">
            <CardSkeleton height={150} />
          </LoadingRegion>
        ) : summary.status === 'error' ? (
          <ErrorState error={summary.error} onRetry={summary.reload} />
        ) : s ? (
          <>
            <section aria-labelledby="rep-flow" className="tm-card">
              <SectionHeader
                title="Money in and out"
                id="rep-flow"
                action={<span className="tm-hint">{s.periodLabel}</span>}
              />
              <div className="tm-flow-grid" style={{ marginBlockStart: 12 }}>
                <div>
                  <p className="tm-eyebrow">Money in</p>
                  <Amount minorUnits={s.moneyInMinor} currency={s.currency} size="lg" direction="in" />
                  <p className="tm-hint" style={{ margin: '4px 0 0' }}>
                    {formatPercentageChange(s.moneyInChangeRatio, s.comparisonLabel).value}{' '}
                    {s.comparisonLabel}
                  </p>
                </div>
                <div>
                  <p className="tm-eyebrow">Money out</p>
                  <Amount minorUnits={s.moneyOutMinor} currency={s.currency} size="lg" />
                  <p className="tm-hint" style={{ margin: '4px 0 0' }}>
                    {formatPercentageChange(s.moneyOutChangeRatio, s.comparisonLabel).value}{' '}
                    {s.comparisonLabel}
                  </p>
                </div>
              </div>
              <hr className="tm-divider" style={{ margin: '14px 0' }} />
              <div className="tm-between">
                <span className="tm-label">Net</span>
                <Amount
                  minorUnits={s.netMinor}
                  currency={s.currency}
                  size="lg"
                  direction={s.netMinor >= 0 ? 'in' : 'out'}
                />
              </div>
            </section>

            <section aria-labelledby="rep-cats">
              <SectionHeader title="Where money went" id="rep-cats" />
              {s.byCategory.length === 0 ? (
                <div className="tm-card">
                  <EmptyState
                    title="Nothing to break down"
                    body="There were no outgoing payments in this period."
                  />
                </div>
              ) : (
                <div className="tm-card">
                  {/* Bar list, not a chart: legible at 320px and at 200% text,
                      and every value is readable as text as well as length. */}
                  <ul className="tm-bars">
                    {s.byCategory.map((c) => (
                      <li key={c.category} className="tm-bar">
                        <div className="tm-between">
                          <span>{c.category}</span>
                          <Amount minorUnits={c.amountMinor} currency={s.currency} />
                        </div>
                        <div
                          className="tm-bar__track"
                          role="img"
                          aria-label={`${c.category}: ${Math.round((c.amountMinor / maxCategory) * 100)}% of the largest category`}
                        >
                          <span
                            className="tm-bar__fill"
                            style={{ inlineSize: `${(c.amountMinor / maxCategory) * 100}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </>
        ) : null}

        <Notice tone="info" title="Detailed reporting is on the web app">
          Custom date ranges, column selection and large exports need a wider screen, so they stay
          on SME Web.
        </Notice>

        {canExport ? (
          <Link to="/mobile/continue-on-web?task=exports" className="tm-btn tm-btn--block">
            Export on web
          </Link>
        ) : (
          <p className="tm-hint">You do not have permission to export reports.</p>
        )}
      </div>
    </>
  );
}
